// P2b tanda 3 — helpers puros de imágenes. La subida/compresión real usa canvas
// (navegador); acá van las piezas deterministas y testeables.
import { decodeHtml } from './decodeHtml';

const MIN_W = 1200;
const MIN_H = 1500;
const MAX_BYTES = 400 * 1024;
// Tanda 3e (ajuste 7): techo de tamaño — más grande que esto se redimensiona
// antes de subir. P3-1 (D1 de Miguel): la conversión es JPG DIRECTO calidad
// 0.85 — el WebP quedó descartado porque este hosting lo rechaza con 406
// (medido 13-sep-2026) y cada foto pagaba DOS subidas y DOS recompresiones.
// Mejora anotada, sin construir: una sonda por tienda que detecte si SU
// hosting acepta WebP y elija formato por tienda.
const MAX_W = 1600;
const MAX_H = 2000;
const JPG_Q = 0.85;

function slug(s) {
  return String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// marca+nombre + tono → marca-nombre-tono.jpg (editable antes de subir).
// P3-1 (punto 3): el nombre puede venir del ESPEJO con entidades HTML
// («PB&amp;J») — se decodifican ANTES del slug, o el archivo queda
// «…-pb-amp-j». Las imágenes elegidas de la mediateca no pasan por acá.
export function normalizeFilename(nombre, variante) {
  const base = [slug(decodeHtml(nombre)), slug(decodeHtml(variante))].filter(Boolean).join('-');
  return `${base || 'producto'}.jpg`;
}

// alt: SOLO marca + nombre + tono/variación ("Marca Nombre tono Arena").
// Tanda 3b (ajuste 5): sin "sobre fondo blanco" ni ninguna descripción de la
// foto que no se pueda saber desde el formulario. P3-1: entidades decodificadas
// («PB&amp;J» → «PB&J») — el alt es texto plano, no HTML.
export function generateAltImagen(nombre, variante) {
  const n = String(decodeHtml(nombre) || '').trim();
  const v = String(decodeHtml(variante) || '').trim();
  return `${n}${v ? ` tono ${v}` : ''}`.trim();
}

// Dimensiones objetivo: nunca por debajo del mínimo (1200×1500) y, desde 3e
// (ajuste 7), nunca por encima de 1600×2000 — más grande se reduce conservando
// la proporción.
export function targetDimensions(natW, natH, minW = MIN_W, minH = MIN_H) {
  if (natW < minW || natH < minH) return { width: natW, height: natH, belowMin: true };
  if (natW > MAX_W || natH > MAX_H) {
    const escala = Math.min(MAX_W / natW, MAX_H / natH);
    return { width: Math.round(natW * escala), height: Math.round(natH * escala), belowMin: false };
  }
  return { width: natW, height: natH, belowMin: false };
}

// Elige la MAYOR calidad cuyo tamaño no supera maxBytes. encodeBytesFn(q)→bytes.
// Si ninguna cumple, devuelve la más baja (nunca null): comprimir lo máximo posible.
export function chooseQuality(encodeBytesFn, maxBytes = MAX_BYTES) {
  const qualities = [0.92, 0.86, 0.8, 0.74, 0.68, 0.62, 0.56, 0.5, 0.44, 0.4];
  for (const q of qualities) {
    if (encodeBytesFn(q) <= maxBytes) return q;
  }
  return qualities[qualities.length - 1];
}

export const IMG_MIN_W = MIN_W;
export const IMG_MIN_H = MIN_H;
export const IMG_MAX_BYTES = MAX_BYTES;
export const JPG_QUALITY = JPG_Q;

// Valida proporción 4:5 (±3%) y mínimo de tamaño. No bloquea: devuelve motivos.
export function validarImagen(width, height) {
  const reasons = [];
  const ratio = width / height;
  const target = 4 / 5;
  if (Math.abs(ratio - target) / target > 0.03) reasons.push('la proporción no es 4:5');
  if (width < MIN_W || height < MIN_H) reasons.push(`es menor a ${MIN_W}×${MIN_H}`);
  return { ok: reasons.length === 0, reasons };
}

// P3-1 (D1): pipeline de subida desde el DISPOSITIVO — convierte UNA sola vez
// a JPG (calidad 0.85), redimensiona al techo 1600×2000 si es más grande y
// baja la calidad si aun así pesa >400 KB. El fondo se rellena BLANCO antes de
// dibujar: un PNG con transparencia exportado a JPG sin relleno queda NEGRO.
// Si el navegador no da canvas, devuelve la original tal cual (mejor subir que
// perder la foto). Las imágenes elegidas de la mediateca NO pasan por acá.
// (El WebP y su reintento 406→JPG de la 3e/3f se retiraron: doble conversión
// y doble subida garantizadas en este hosting.)
export function procesarImagenParaSubir(dataUrl, { maxBytes = MAX_BYTES } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const { width, height } = targetDimensions(img.naturalWidth, img.naturalHeight);
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext && canvas.getContext('2d');
        if (!ctx) return resolve(dataUrl);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const bytesDe = (d) => Math.round((d.length - d.indexOf(',') - 1) * 3 / 4);
        let out = canvas.toDataURL('image/jpeg', JPG_Q);
        if (bytesDe(out) > maxBytes) {
          const q = chooseQuality((qq) => bytesDe(canvas.toDataURL('image/jpeg', qq)), maxBytes);
          out = canvas.toDataURL('image/jpeg', q);
        }
        resolve(out);
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// Comprime un dataURL con canvas hasta ≤ maxBytes sin bajar del mínimo.
// (Se ejecuta en el navegador; no se testea unitariamente —las piezas puras sí.)
export function comprimirDataUrl(dataUrl, { maxBytes = MAX_BYTES } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = targetDimensions(img.naturalWidth, img.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const encodeBytes = (q) => {
        const d = canvas.toDataURL('image/jpeg', q);
        return Math.round((d.length - d.indexOf(',') - 1) * 3 / 4);
      };
      const approxBytes = Math.round((dataUrl.length - dataUrl.indexOf(',') - 1) * 3 / 4);
      if (approxBytes <= maxBytes) return resolve(dataUrl);
      const q = chooseQuality(encodeBytes, maxBytes);
      resolve(canvas.toDataURL('image/jpeg', q));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}
